import React, { forwardRef, useState } from 'react'
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons'

interface BerlixInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  icon?: React.ReactNode
  error?: boolean
  loading?: boolean
  label?: string
  helper?: string
  required?: boolean
  value?: string | number | readonly string[]
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

// Fixed BerlixInput that properly handles Ant Design Form integration
export const BerlixInput = forwardRef<HTMLInputElement, BerlixInputProps>(
  ({ icon, error, loading, className = '', value, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
    }

    return (
      <div className="berlix-input-wrapper">
        {icon && <span className="berlix-input-icon">{icon}</span>}
        <input
          ref={ref}
          value={value || ''}
          onChange={handleChange}
          className={`berlix-input ${icon ? 'with-icon' : ''} ${error ? 'error' : ''} ${loading ? 'loading' : ''} ${className}`}
          {...props}
        />
      </div>
    )
  }
)

BerlixInput.displayName = 'BerlixInput'

interface BerlixPasswordProps extends Omit<BerlixInputProps, 'type'> {
  visibilityToggle?: boolean
}

export const BerlixPassword = forwardRef<HTMLInputElement, BerlixPasswordProps>(
  ({ icon, error, loading, visibilityToggle = true, className = '', value, onChange, ...props }, ref) => {
    const [visible, setVisible] = useState(false)
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
    }
    
    return (
      <div className="berlix-input-wrapper berlix-password-wrapper">
        {icon && <span className="berlix-input-icon">{icon}</span>}
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          value={value || ''}
          onChange={handleChange}
          className={`berlix-input ${icon ? 'with-icon' : ''} ${error ? 'error' : ''} ${loading ? 'loading' : ''} ${className}`}
          {...props}
        />
        {visibilityToggle && (
          <button
            type="button"
            className="berlix-password-toggle"
            onClick={() => setVisible(!visible)}
            tabIndex={-1}
          >
            {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </button>
        )}
      </div>
    )
  }
)

BerlixPassword.displayName = 'BerlixPassword'

interface BerlixTextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> {
  error?: boolean
  loading?: boolean
  value?: string | number | readonly string[]
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}

export const BerlixTextArea = forwardRef<HTMLTextAreaElement, BerlixTextAreaProps>(
  ({ error, loading, className = '', value, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e)
    }

    return (
      <textarea
        ref={ref}
        value={value || ''}
        onChange={handleChange}
        className={`berlix-input berlix-textarea ${error ? 'error' : ''} ${loading ? 'loading' : ''} ${className}`}
        {...props}
      />
    )
  }
)

BerlixTextArea.displayName = 'BerlixTextArea'

// Form Item wrapper for better integration
interface BerlixFormItemProps {
  label?: string
  required?: boolean
  error?: string
  helper?: string
  children: React.ReactNode
}

export const BerlixFormItem: React.FC<BerlixFormItemProps> = ({
  label,
  required,
  error,
  helper,
  children
}) => {
  return (
    <div className="berlix-form-item">
      {label && (
        <label className={`berlix-label ${required ? 'required' : ''}`}>
          {label}
        </label>
      )}
      {children}
      {(error || helper) && (
        <div className={`berlix-helper-text ${error ? 'error' : ''}`}>
          {error || helper}
        </div>
      )}
    </div>
  )
}